// src/pages/Dashboard/Tabs/TabAnalisisArticulos.js
import React, { useState, useEffect, useCallback } from 'react';
import api from '../../../services/api';
import FilterBarArticulos from '../../../components/Dashboard/FilterBarArticulos';
import ComprasChart from '../../../components/Dashboard/ComprasChart';
import KpiCard from '../../../components/Dashboard/KpiCard';
import { formatMoney, formatMoneyCorto, formatNumber, formatFecha } from '../../../utils/formato';

// La empresa la elige el Dashboard y baja como prop.
const TabAnalisisArticulos = ({ empresa = process.env.REACT_APP_EMPRESA_DEFAULT || 'CIMSA' }) => {

  const [filters, setFilters] = useState({ dateRange: '30d', articulo: '' });
  const [fetchTrigger, setFetchTrigger] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isFilterLoading, setIsFilterLoading] = useState(false);
  const [error, setError] = useState(null);
  const [datos, setDatos] = useState(null);

  const paramsActuales = useCallback(() => ({
    empresa,
    rango: filters.dateRange,
    articulo: filters.articulo || undefined
  }), [empresa, filters]);

  useEffect(() => {
    let cancelado = false;

    const cargarArticulos = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const { data } = await api.get('/compras/dashboard/articulos', { params: paramsActuales() });
        if (!cancelado) setDatos(data);
      } catch (err) {
        if (!cancelado) {
          console.error('Error cargando el análisis de artículos:', err);
          setError(err.response?.data?.error || 'No se pudo cargar el análisis de artículos.');
        }
      } finally {
        if (!cancelado) {
          setIsLoading(false);
          setIsFilterLoading(false);
        }
      }
    };

    cargarArticulos();
    return () => { cancelado = true; };

    // La recarga se dispara con el botón "Aplicar", no en cada tecleo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchTrigger, empresa]);

  const handleApplyFilters = () => {
    setIsFilterLoading(true);
    setFetchTrigger(t => t + 1);
  };

  const datosGrafico = datos && {
    labels: datos.topArticulos.labels,
    datasets: [{ label: 'Monto Total', data: datos.topArticulos.data, backgroundColor: '#1A3BFF', borderRadius: 4 }]
  };

  return (
    <div className="mt-4">
      <FilterBarArticulos
        filters={filters}
        setFilters={setFilters}
        onApply={handleApplyFilters}
        isLoading={isFilterLoading}
      />

      {isLoading ? (
        <div className="text-center my-5">
          <div className="spinner-border text-primary" role="status"></div>
          <p className="mt-2">Cargando Análisis de Artículos...</p>
        </div>
      ) : error ? (
        <div className="alert alert-danger">{error}</div>
      ) : datos && (
        <>
          <p className="text-muted small mb-2">
            <i className="bi bi-building me-1"></i>
            Empresa: <strong>{empresa}</strong>
            <span className="mx-2">·</span>
            <i className="bi bi-calendar3 me-1"></i>
            Período: {formatFecha(datos.periodo.desde)} — {formatFecha(datos.periodo.hasta)}
            {filters.articulo && <> · Artículo: <strong>{filters.articulo}</strong></>}
          </p>

          <div className="row">
            <KpiCard
              title="Artículos Distintos"
              value={formatNumber(datos.kpis.articulosDistintos)}
              icon="bi-box-seam"
              color="blue"
            />
            <KpiCard
              title="Unidades Compradas"
              value={formatNumber(Math.round(datos.kpis.unidadesCompradas))}
              icon="bi-boxes"
              color="black"
            />
            <KpiCard
              title="Monto en Artículos"
              value={formatMoneyCorto(datos.kpis.montoTotal)}
              // Las líneas de la OC son netas: este total no coincide con el de
              // la pestaña Resumen, que sale de DocTotal (con IVA).
              subtitle={`${formatMoney(datos.kpis.montoTotal)} · neto sin IVA`}
              icon="bi-cash-stack"
              color="green"
            />
          </div>

          <div className="row mt-2">
            <div className="col-12 mb-3">
              <div className="card shadow-sm">
                <div className="card-body">
                  <ComprasChart
                    chartData={datosGrafico}
                    title="Top 10 Artículos por Monto Comprado"
                    horizontal
                    height={420}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Tabla de detalle: lo que el gráfico resume, con el precio promedio
              ponderado para detectar variaciones entre compras. */}
          <div className="card shadow-sm">
            <div className="card-body">
              <h5 className="card-title mb-3">
                Detalle por Artículo
                <span className="text-muted fs-6 fw-normal ms-2">
                  ({datos.ranking.length} {datos.ranking.length === 1 ? 'artículo' : 'artículos'})
                </span>
              </h5>

              {datos.ranking.length === 0 ? (
                <p className="text-muted mb-0">No se compraron artículos en el período seleccionado.</p>
              ) : (
                <div className="table-responsive" style={{ maxHeight: '460px' }}>
                  <table className="table table-sm table-hover align-middle">
                    <thead className="table-light sticky-top">
                      <tr>
                        <th>Código</th>
                        <th>Descripción</th>
                        <th className="text-end">Cantidad</th>
                        <th className="text-end">Precio Prom.</th>
                        <th className="text-end">Monto</th>
                        <th className="text-center">Órdenes</th>
                        <th className="text-center">Proveedores</th>
                      </tr>
                    </thead>
                    <tbody>
                      {datos.ranking.map(a => (
                        <tr key={a.codigo}>
                          <td><code>{a.codigo}</code></td>
                          <td>{a.descripcion}</td>
                          <td className="text-end">{formatNumber(Math.round(a.cantidad))}</td>
                          <td className="text-end">{formatMoney(a.precioPromedio)}</td>
                          <td className="text-end fw-semibold">{formatMoney(a.monto)}</td>
                          <td className="text-center">{a.ordenes}</td>
                          <td className="text-center">{a.proveedores}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default TabAnalisisArticulos;
